import { useAuthStore } from '@/features/auth/useAuthStore';
import { Layout } from '@/features/dashboard/layout/Layout'
import { fetchMaintenanceStatus } from '@/features/maintenance/maintenanceApi';
import { createFileRoute, redirect } from '@tanstack/react-router'


// ロールごとのリダイレクト先
const REDIRECT_PATHS = {
  'admin': '/',
  'san_admin': '/',
  'san_retailer_viewer': '/',
  'billing_viewer': '/billing',
} as const;

// パスとロールのマッピング（完全一致）
const CHECK_PATHS = {
  '/identifications': [          // 本人確認一覧画面
    'san_admin',
    'san_retailer_viewer',
  ],
  '/sim-distribution': [   // SIM分配画面
    'san_admin',
  ],
  '/delivery-requests': [   // 発注依頼画面
    'admin',
    'san_admin',
  ],
  '/delivery-requests/create': [   // 発注依頼作成画面
    'admin',
    'san_admin',
  ],
  '/billing': [             // 請求データ画面
    'billing_viewer',
  ],
  '/billing-management': [  // 課金権限管理画面
    'billing_viewer',
  ],
  '/call-details': [        // 通話明細管理画面
    'billing_viewer',
  ],
  '/continuous-payment-confirmation': [  // 継続課金支払確認画面
    'billing_viewer',
  ],
  '/nttpc-maintenances': [  // メンテナンス設定（billing_viewer のみ変更可）
    'billing_viewer',
  ],
} as const;

// パスパターンとロールのマッピング（正規表現）
const CHECK_PATERN_PATHS = {
  '^\\/clients\\/\\d+\\/identification\\/edit$': [              // 本人確認編集画面
    'san_admin',
    'san_retailer_viewer',
  ],
  '^\\/clients\\/\\d+\\/identification\\/reupload$': [         // 本人確認画像再アップロード画面
    'san_admin',
    'san_retailer_viewer',
  ],
  '^\\/clients\\/\\d+\\/identification\\/histories\\/\\d+$': [ // 本人確認履歴詳細画面
    'san_admin',
    'san_retailer_viewer',
  ],
  '^\\/clients\\/\\d+\\/corporate-info\\/edit$': [              // 代表者/担当者情報編集画面
    'san_admin',
    'san_retailer_viewer',
  ],
  '^\\/delivery-requests\\/\\d+\\/shipping-history$': [   // 発注履歴
    'admin',
    'san_admin',
  ],
} as const;

// メンテナンス中も利用可能なロール
const EXIST_MAINTENANCE_MODE_CHECK: readonly string[] = [
  'billing_viewer',
] as const;

// billing_viewerがアクセス可能なパス一覧（CHECK_PATHSから抽出）
const BILLING_VIEWER_ALLOWED_PATHS = Object.keys(CHECK_PATHS).filter(
  path => (CHECK_PATHS[path as keyof typeof CHECK_PATHS] as readonly string[]).includes('billing_viewer')
) as readonly string[];

export const Route = createFileRoute('/_dashboard')({
  beforeLoad: async ({ location }) => {
    const authStore = useAuthStore.getState();
    await authStore.verifyLogin();
    // verifyLogin()完了後に最新の状態を取得
    const { adminRole } = useAuthStore.getState();
    const { pathname } = location;

    if (!adminRole) {
      throw redirect({ to: '/' });
    }

    const redirectTo = REDIRECT_PATHS[adminRole as keyof typeof REDIRECT_PATHS] || '/';
    
    // 完全一致パスのチェック
    for (const [path, roles] of Object.entries(CHECK_PATHS)) {
    if (path === pathname) {
        const allowedRoles = roles as readonly string[];
        
        // 権限がない場合はリダイレクト
        if (!allowedRoles.includes(adminRole)) {
          throw redirect({ to: redirectTo });
        }
      }
    }

    // パターンマッチングパスのチェック
    for (const [patternStr, roles] of Object.entries(CHECK_PATERN_PATHS)) {
      const pattern = new RegExp(patternStr);

      if (pattern.test(pathname)) {
        const allowedRoles = roles as readonly string[];
        
        // パスにアクセス権限があるかチェック
        if (!allowedRoles.includes(adminRole)) {
          throw redirect({ to: redirectTo });
        }
      }
    }

    // 定義されていないパスへのアクセス制御
    // billing_viewerは許可されたパスにのみアクセス可能（定数から判定）
    if (adminRole === 'billing_viewer' && !BILLING_VIEWER_ALLOWED_PATHS.includes(pathname)) {
      throw redirect({ to: redirectTo });
    }

    // ロールが未設定の場合、ダッシュボードにリダイレクト
    if (authStore.adminRole === undefined) {
      throw redirect({ to: '/' });
    }

    // メンテナンス中: billing_viewer 以外はメンテナンス画面へ
    const maintenanceStatus = await fetchMaintenanceStatus();
    if (
      maintenanceStatus.active &&
      !EXIST_MAINTENANCE_MODE_CHECK.includes(adminRole)
    ) {
      throw redirect({ to: '/maintenance' });
    }
  },
  component: Layout
});
